import { useParams } from 'react-router-dom'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'

/**
 * The resolved Mongo project id for the current route.
 *
 * In kiosk mode the URL param already IS the project id, so every source
 * agrees. In digital mode the URL param is a *slug*; the real id is resolved
 * once by `ProjectRoute` (slug → id via `/api/projects/by-slug`) and lands in
 * the loaded config (`config._id`) and the session store (`sessionState.projectId`).
 *
 * Resolution order: `config._id` (set before `View` mounts) → session store →
 * raw route param (last-resort fallback / kiosk pass-through).
 *
 * USE THIS for anything keyed by project id: API calls (`?id=`, `?projectId=`),
 * store writes, version-cache keys, telemetry. Do NOT use it for navigation —
 * `navigate("/${routeParam}/...")` must keep the raw param so the slug stays in
 * the address bar in digital mode.
 */
export function useResolvedProjectId(): string | undefined {
  const configId = useConfigStore((s) => s.config?._id)
  const sessionProjectId = useSessionStateStore((s) => s.projectId)
  const { projectId: routeParam } = useParams()
  return configId || sessionProjectId || routeParam
}
